import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, User as UserIcon, Building2, MapPin, Phone, Mail, Landmark } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { COUNTRIES, COUNTRIES_BY_CODE, TAX_KIND_BY_COUNTRY } from "@/lib/countries";
import { PasskeysManageCard } from "@/components/auth/PasskeysManageCard";

interface Address {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

interface ProfilePayload {
  user_id: string;
  email: string;
  name: string;
  phone: string | null;
  phone_country: string | null;
  phone_verified: boolean;
  company_name: string | null;
  company_industry: string | null;
  company_size: string | null;
  billing_address: Address | null;
  tax_id: string | null;
  tax_id_kind: string | null;
}

const EMPTY_ADDRESS: Address = {
  line1: "", line2: "", city: "", state: "", postcode: "", country: "US",
};

const INDUSTRY_OPTIONS = [
  "Creator / influencer",
  "Direct-to-consumer brand",
  "Agency",
  "Marketplace seller",
  "SaaS",
  "Media / publisher",
  "Non-profit",
  "Other",
];

const SIZE_OPTIONS = ["1", "2–10", "11–50", "51–200", "201–500", "500+"];

export const Profile: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<ProfilePayload>({
    queryKey: ["profile"],
    queryFn: async () => (await api.get("/auth/profile")).data,
  });

  const [name, setName] = React.useState("");
  const [phoneCountry, setPhoneCountry] = React.useState("US");
  const [phoneLocal, setPhoneLocal] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");
  const [companyIndustry, setCompanyIndustry] = React.useState("");
  const [companySize, setCompanySize] = React.useState("");
  const [address, setAddress] = React.useState<Address>(EMPTY_ADDRESS);
  const [taxId, setTaxId] = React.useState("");

  const isSeeded = React.useRef(false);
  React.useEffect(() => {
    if (isSeeded.current || !q.data) return;
    const d = q.data;
    isSeeded.current = true;
    setName(d.name || "");
    setPhoneCountry(d.phone_country || "US");
    // Strip the dial-code from the stored phone to display just the local number
    const country = COUNTRIES_BY_CODE[d.phone_country || "US"];
    const stored = (d.phone || "").trim();
    setPhoneLocal(country && stored.startsWith(country.dial) ? stored.slice(country.dial.length) : stored);
    setCompanyName(d.company_name || "");
    setCompanyIndustry(d.company_industry || "");
    setCompanySize(d.company_size || "");
    setAddress(d.billing_address || EMPTY_ADDRESS);
    setTaxId(d.tax_id || "");
  }, [q.data]);

  const country = COUNTRIES_BY_CODE[phoneCountry] || COUNTRIES_BY_CODE["US"];
  const billingCountry = address.country || phoneCountry;
  const taxKindConfig = TAX_KIND_BY_COUNTRY[billingCountry] || {
    kind: "OTHER",
    label: "Tax ID",
    placeholder: "e.g. VAT / GST / EIN",
  };

  const save = useMutation({
    mutationFn: async () => {
      const digits = phoneLocal.replace(/[^0-9]/g, "");
      const fullPhone = digits ? `${country.dial}${digits}` : null;
      const payload = {
        name: name.trim() || undefined,
        phone: fullPhone,
        phone_country: phoneCountry,
        company_name: companyName.trim() || null,
        company_industry: companyIndustry || null,
        company_size: companySize || null,
        billing_address: address,
        tax_id: taxId.trim() || null,
        tax_id_kind: taxId.trim() ? taxKindConfig.kind : null,
      };
      return (await api.put("/auth/profile", payload)).data;
    },
    onSuccess: () => {
      toast.success("Profile saved");
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Could not save profile"),
  });

  const setAddr = (k: keyof Address, v: string) => setAddress((a) => ({ ...a, [k]: v }));

  return (
    <DashboardLayout
      title="Profile"
      subtitle="Manage your account details and billing information."
      seoPath="/profile"
      seoDescription="Update your SociBaba profile, company details, phone and country-aware tax ID."
    >
      {q.isLoading ? (
        <div className="sg-card p-8 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-[color:var(--sg-muted)]" />
        </div>
      ) : (
        <div className="space-y-6" data-testid="profile-page">
          {/* Personal */}
          <div className="sg-card p-6">
            <div className="flex items-start gap-3 mb-5">
              <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                <UserIcon className="h-5 w-5 text-violet-700" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold tracking-tight">Personal</div>
                <p className="text-sm text-[color:var(--sg-muted)] mt-1">
                  Your name and contact details. Email cannot be changed here — contact support if needed.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Full name</Label>
                <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-white" placeholder="Your full name" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)] flex items-center gap-1.5">
                  <Mail className="h-3 w-3" /> Email
                </Label>
                <Input value={q.data?.email || ""} disabled className="mt-1.5 bg-[color:var(--sg-muted-bg)]" data-testid="profile-email" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)] flex items-center gap-1.5">
                  <Phone className="h-3 w-3" /> Mobile number
                </Label>
                <div className="flex gap-2 mt-1.5">
                  <select
                    value={phoneCountry}
                    onChange={(e) => setPhoneCountry(e.target.value)}
                    data-testid="profile-phone-country"
                    className="h-10 px-2 rounded-md border border-[color:var(--sg-border)] bg-white text-sm min-w-[120px]"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} {c.dial}
                      </option>
                    ))}
                  </select>
                  <Input
                    data-testid="profile-phone-local"
                    value={phoneLocal}
                    onChange={(e) => setPhoneLocal(e.target.value)}
                    className="flex-1 bg-white"
                    placeholder="9876543210"
                    inputMode="numeric"
                  />
                </div>
                <div className="text-[11px] text-[color:var(--sg-muted)] mt-1.5">
                  Full number: <span className="font-mono">{country.dial}{phoneLocal.replace(/[^0-9]/g, "")}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Company */}
          <div className="sg-card p-6">
            <div className="flex items-start gap-3 mb-5">
              <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <Building2 className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold tracking-tight">Company</div>
                <p className="text-sm text-[color:var(--sg-muted)] mt-1">
                  Optional — helps us tailor onboarding and billing invoices.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Company name</Label>
                <Input data-testid="profile-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="mt-1.5 bg-white" placeholder="Acme Inc." />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Industry</Label>
                <select value={companyIndustry} onChange={(e) => setCompanyIndustry(e.target.value)} data-testid="profile-company-industry" className="mt-1.5 w-full h-10 px-2 rounded-md border border-[color:var(--sg-border)] bg-white text-sm">
                  <option value="">Select…</option>
                  {INDUSTRY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Team size</Label>
                <select value={companySize} onChange={(e) => setCompanySize(e.target.value)} data-testid="profile-company-size" className="mt-1.5 w-full h-10 px-2 rounded-md border border-[color:var(--sg-border)] bg-white text-sm">
                  <option value="">Select…</option>
                  {SIZE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Billing address + Tax */}
          <div className="sg-card p-6">
            <div className="flex items-start gap-3 mb-5">
              <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold tracking-tight">Billing address & tax</div>
                <p className="text-sm text-[color:var(--sg-muted)] mt-1">
                  Used on invoices. The tax field auto-adapts to your billing country — GST for India, VAT for Europe, EIN for the US, etc.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Address line 1</Label>
                <Input data-testid="profile-addr-line1" value={address.line1} onChange={(e) => setAddr("line1", e.target.value)} className="mt-1.5 bg-white" placeholder="Street & number" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Address line 2</Label>
                <Input data-testid="profile-addr-line2" value={address.line2} onChange={(e) => setAddr("line2", e.target.value)} className="mt-1.5 bg-white" placeholder="Apartment, suite, unit (optional)" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">City</Label>
                <Input data-testid="profile-addr-city" value={address.city} onChange={(e) => setAddr("city", e.target.value)} className="mt-1.5 bg-white" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">State / region</Label>
                <Input data-testid="profile-addr-state" value={address.state} onChange={(e) => setAddr("state", e.target.value)} className="mt-1.5 bg-white" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Postal code</Label>
                <Input data-testid="profile-addr-postcode" value={address.postcode} onChange={(e) => setAddr("postcode", e.target.value)} className="mt-1.5 bg-white" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Country</Label>
                <select value={address.country} onChange={(e) => setAddr("country", e.target.value)} data-testid="profile-addr-country" className="mt-1.5 w-full h-10 px-2 rounded-md border border-[color:var(--sg-border)] bg-white text-sm">
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                </select>
              </div>
              {/* Smart Tax ID field */}
              <div className="md:col-span-2 border-t border-[color:var(--sg-border)] pt-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)] flex items-center gap-1.5">
                  <Landmark className="h-3 w-3" />
                  {taxKindConfig.label}
                  <span className="text-[10px] font-normal text-[color:var(--sg-muted)] normal-case ml-1">· auto-detected from billing country</span>
                </Label>
                <Input
                  data-testid="profile-tax-id"
                  value={taxId}
                  onChange={(e) => setTaxId(e.target.value)}
                  className="mt-1.5 bg-white font-mono"
                  placeholder={taxKindConfig.placeholder}
                />
                <div className="text-[11px] text-[color:var(--sg-muted)] mt-1.5">
                  Optional. Include this if your company needs the tax ID printed on invoices.
                </div>
              </div>
            </div>
          </div>

          {/* Save */}
          <div className="flex justify-end sticky bottom-4">
            <Button
              data-testid="profile-save"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white shadow-lg"
            >
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save profile
            </Button>
          </div>

          {/* Passkeys */}
          <PasskeysManageCard />
        </div>
      )}
    </DashboardLayout>
  );
};

export default Profile;
